import { type HttpClientConfig, type HttpClientInterface } from 'core/infra/http/HttpClient'

import { type Load } from '../../domain/Load'
import { type RawTender, mapApiToLoad } from './TenderMapper'

export type TenderRepositoryDependencies = {
  httpClient: HttpClientInterface
  downloadAttachmentHttpClient?: HttpClientInterface
}

export type RejectTenderParams = {
  id: string
  rejectReason: string
}

export type TenderAttachmentParams = {
  quoteUuid: string
  attachmentId: number
}

export class TenderRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient, downloadAttachmentHttpClient }: TenderRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getTenderById(id: string, config?: Partial<HttpClientConfig>): Promise<Load> {
    const endpoint = `/api/v2/shipperguide/tenders/${id}`

    const response = await this.httpClient.get<RawTender>(endpoint, config)

    try {
      return mapApiToLoad(response.data)
    } catch (error) {
      throw new Error('Failed to map API return to Tender', { cause: error as Error })
    }
  }

  async acceptTender(id: string, config?: Partial<HttpClientConfig>): Promise<void> {
    const endpoint = `/api/v2/shipperguide/shipments/tender-confirmation/${id}/accept`

    await this.httpClient.patch(endpoint, config)
  }

  async rejectTender(
    { rejectReason, id }: RejectTenderParams,
    config?: Partial<HttpClientConfig>
  ): Promise<void> {
    const endpoint = `/api/v2/shipperguide/shipments/tender-confirmation/${id}/reject`

    const payload = { reject_reason: rejectReason }

    await this.httpClient.patch(endpoint, payload, config)
  }

  async getPendingTenders(config?: Partial<HttpClientConfig>): Promise<Array<Load>> {
    const endpoint = '/api/v2/shipperguide/tenders/pending'

    const response = await this.httpClient.get<Array<RawTender>>(endpoint, config)

    try {
      return response.data.map(mapApiToLoad)
    } catch (error) {
      throw new Error('Failed to map list of API return to Tender', { cause: error as Error })
    }
  }
}
